import * as adminAffiliatesApi from '@apis/admin-affiliates';
import * as adminUserApi from '@apis/admin-user';
import { AffiliateListItem, PaymentStatementWithAttribution } from '@models/affiliate';
import { PaymentStatement } from '@models/paymentStatement';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Badge, Button, Modal, Spin, Switch, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

const REFUND_STATUSES: { [key: string]: React.ReactNode } = {
  all: <div style={{ color: 'red' }}>전체 환불</div>,
  partial: <div style={{ color: '#d944db' }}>부분 환불</div>,
  requested: <div style={{ color: '#db9737' }}>환불 요청</div>,
  accepted: <div style={{ color: '#2782b3' }}>환불 예정</div>,
  denied: <div style={{ color: '#6e6e6e' }}>환불 반려</div>,
  reserved: <div style={{ color: 'green' }}>환불 예약</div>,
  request_cancelled: <div style={{ color: 'tomato' }}>환불 철회</div>,
  submission: <div style={{ color: 'pink' }}>환불 접수</div>,
  none: <div>결제 완료</div>,
};

const formatNumber = (n?: number | null) => (typeof n === 'number' ? n.toLocaleString() : '-');

const TableTotalCount: React.FC<{ total?: number }> = ({ total }) => (
  <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>총 {formatNumber(total ?? 0)}건</div>
);

const copyToastBodyStyle = {
  color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
  textAlign: 'center' as const,
  fontFamily: 'Pretendard',
  fontSize: '14px',
  fontStyle: 'normal' as const,
  fontWeight: 500,
  lineHeight: '20px',
};

interface UserIssuedAffiliatesSectionProps {
  userId: string;
}

export const UserIssuedAffiliatesSection: React.FC<UserIssuedAffiliatesSectionProps> = ({ userId }) => {
  const dialog = useOkCancelDialog();
  const [activeTab, setActiveTab] = useState<'product' | 'campaign'>('product');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPaymentStatements, setSelectedPaymentStatements] = useState<PaymentStatement[]>([]);

  const query = useQuery({
    queryKey: ['user-issued-affiliates', userId, activeTab, page, size],
    queryFn: () =>
      adminUserApi.getUserAffiliates(userId, {
        targetType: activeTab,
        page,
        size,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      }),
    enabled: !!userId,
  });

  const updateStatusMutation = useMutation({
    mutationFn: adminAffiliatesApi.updateAffiliateStatus,
    onSuccess: (data) => {
      query.refetch();
      toast.success(`Affiliate가 ${data.isActive ? '활성화' : '비활성화'}되었습니다.`);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || '상태 업데이트에 실패했습니다.');
    },
  });

  const result = query.data?.data;
  const items: AffiliateListItem[] = result?.contents || [];

  const handleToggleStatus = (affiliateId: string, currentStatus: boolean) => {
    dialog.open({
      content: `Affiliate를 ${currentStatus ? '비활성화' : '활성화'}하시겠습니까?`,
      onConfirm: () => {
        updateStatusMutation.mutate({
          affiliateId,
          isActive: !currentStatus,
        });
      },
    });
  };

  const productOnlyColumns: ColumnsType<AffiliateListItem> = useMemo(
    () => [
      {
        title: '결제/환불 금액',
        width: 120,
        align: 'right' as const,
        render: (record: AffiliateListItem) => (
          <div>
            {(record.totalPaymentAmount || 0).toLocaleString()}
            <div style={{ color: 'red' }}>{`환불: (${(record.totalRefundAmount || 0).toLocaleString()})`}</div>
          </div>
        ),
      },
      {
        title: '적립 예정 포인트',
        width: 150,
        align: 'right' as const,
        render: (record: AffiliateListItem) => {
          const accumulatedPointAmount = record.accumulatedPointAmount || 0;
          const netAmount = (record.totalPointAmount || 0) - (record.refundPointAmount || 0) - accumulatedPointAmount;
          return `${(netAmount || 0).toLocaleString()}P`;
        },
      },
    ],
    [],
  );

  const columns: ColumnsType<AffiliateListItem> = useMemo(() => {
    const cols: ColumnsType<AffiliateListItem> = [
      {
        title: 'Affiliate ID',
        dataIndex: 'affiliateId',
        width: 100,
        render: (affiliateId: string) => (
          <Button
            size="small"
            onClick={() => {
              navigator.clipboard.writeText(affiliateId);
              toast.dark('Affiliate ID를 클립보드에 복사했습니다', {
                position: 'top-center',
                autoClose: 3000,
                bodyStyle: copyToastBodyStyle,
                theme: 'dark',
              });
            }}>
            ID 복사
          </Button>
        ),
      },
      {
        title: activeTab === 'campaign' ? '캠페인명' : '상품명',
        width: 200,
        render: (record: AffiliateListItem) => record.targetName || record.productName || '-',
      },
      {
        title: '링크 URL',
        dataIndex: 'linkUrl',
        width: 120,
        render: (linkUrl: string) => (
          <Button
            size="small"
            disabled={!linkUrl}
            onClick={() => {
              navigator.clipboard.writeText(linkUrl);
              toast.dark('링크 URL을 클립보드에 복사했습니다', {
                position: 'top-center',
                autoClose: 3000,
                bodyStyle: copyToastBodyStyle,
                theme: 'dark',
              });
            }}>
            복사하기
          </Button>
        ),
      },
      {
        title: '활성화 상태',
        dataIndex: 'isActive',
        width: 120,
        render: (isActive: boolean, record: AffiliateListItem) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Badge status={isActive ? 'success' : 'default'} text={isActive ? '활성화' : '비활성화'} />
            <Switch
              size="small"
              checked={isActive}
              onChange={() => handleToggleStatus(record.affiliateId, isActive)}
              loading={updateStatusMutation.isPending}
            />
          </div>
        ),
      },
      {
        title: '방문 수',
        dataIndex: 'visitCount',
        width: 90,
        align: 'right',
        render: (visitCount: number) => (visitCount ?? 0).toLocaleString(),
      },
      {
        title: activeTab === 'campaign' ? '적립수' : '결제 완료 수',
        width: 120,
        align: 'right',
        render: (record: AffiliateListItem) => (
          <div>
            <div>
              {(record.paymentCount || 0).toLocaleString()}
              {(record.refundCount || 0) > 0 && (
                <div style={{ color: 'red' }}>{`환불: (${record.refundCount.toLocaleString()})`}</div>
              )}
            </div>
            {activeTab === 'product' && (
              <Button
                type="link"
                size="small"
                onClick={() => {
                  setSelectedPaymentStatements(record.paymentStatements || []);
                  setIsModalOpen(true);
                }}
                style={{ padding: 0, marginTop: 4 }}>
                상세보기
              </Button>
            )}
          </div>
        ),
      },
      ...(activeTab === 'product' ? productOnlyColumns : []),
      {
        title: '적립 완료 포인트',
        width: 130,
        align: 'right',
        render: (record: AffiliateListItem) => `${(record.accumulatedPointAmount || 0).toLocaleString()}P`,
      },
      {
        title: '생성일',
        dataIndex: 'createdAt',
        width: 150,
        render: (createdAt: Date) => (createdAt ? moment(createdAt).format('YYYY.MM.DD HH:mm') : '-'),
      },
      {
        title: '비활성화일',
        dataIndex: 'deactivatedAt',
        width: 150,
        render: (deactivatedAt?: Date) => (deactivatedAt ? moment(deactivatedAt).format('YYYY.MM.DD HH:mm') : '-'),
      },
    ];
    return cols;
  }, [activeTab, productOnlyColumns, updateStatusMutation.isPending]);

  const paymentStatementColumns: ColumnsType<PaymentStatementWithAttribution> = [
    {
      title: '구매자명',
      width: 120,
      render: (record: PaymentStatementWithAttribution) => (
        <div>
          <div>{record.buyer?.name || '-'}</div>
          <div style={{ fontSize: 11 }}>{record.buyer?.phone || '-'}</div>
        </div>
      ),
    },
    {
      title: '결제 금액',
      width: 120,
      align: 'right',
      render: (record: PaymentStatementWithAttribution) => (
        <div>
          <div>
            {(record.payment?.amount || 0).toLocaleString()}원
            {record.payment?.point ? `(${record.payment.point.toLocaleString()}P)` : ''}
          </div>
          <Button
            size="small"
            onClick={() => {
              navigator.clipboard.writeText(record.uuid || '');
              toast.dark('주문번호를 클립보드에 복사했습니다', {
                position: 'top-center',
                autoClose: 3000,
                bodyStyle: copyToastBodyStyle,
                theme: 'dark',
              });
            }}>
            주문번호 복사
          </Button>
        </div>
      ),
    },
    {
      title: '결제상태',
      dataIndex: ['statuses', 'refund'],
      width: 100,
      align: 'center',
      render: (status: string) => REFUND_STATUSES[status] || '-',
    },
    {
      title: '결제일시',
      dataIndex: ['payment', 'paidAt'],
      width: 150,
      render: (paidAt: Date) => (paidAt ? moment(paidAt).format('YYYY.MM.DD HH:mm') : '-'),
    },
    {
      title: '적립예정일시',
      dataIndex: ['attribution', 'pointAccumulationScheduledAt'],
      width: 150,
      render: (scheduledAt: Date) => (scheduledAt ? moment(scheduledAt).format('YYYY.MM.DD HH:mm') : '-'),
    },
    {
      title: '적립 포인트',
      dataIndex: ['attribution', 'pointAmount'],
      width: 100,
      align: 'right',
      render: (pointAmount: number) => `${(pointAmount || 0).toLocaleString()}P`,
    },
    {
      title: '적립여부',
      dataIndex: ['attribution', 'pointAccumulated'],
      width: 90,
      align: 'center',
      render: (pointAccumulated: boolean) => (pointAccumulated ? '적립' : '미적립'),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button
          size="small"
          type={activeTab === 'product' ? 'primary' : 'default'}
          onClick={() => {
            setActiveTab('product');
            setPage(0);
          }}>
          상품
        </Button>
        <Button
          size="small"
          type={activeTab === 'campaign' ? 'primary' : 'default'}
          onClick={() => {
            setActiveTab('campaign');
            setPage(0);
          }}>
          캠페인
        </Button>
      </div>

      {query.isLoading ? (
        <div style={{ padding: 30, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : (
        <>
          <TableTotalCount total={result?.total} />
          <Table<AffiliateListItem>
            rowKey="affiliateId"
            columns={columns}
            dataSource={items}
            size="small"
            scroll={{ x: 'max-content' }}
            pagination={{
              current: page + 1,
              pageSize: size,
              total: result?.total || 0,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              onChange: (nextPage, nextSize) => {
                setPage((nextPage || 1) - 1);
                setSize(nextSize || 10);
              },
            }}
          />
        </>
      )}

      <Modal
        title="결제 내역 상세"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsModalOpen(false)}>
            닫기
          </Button>,
        ]}
        width={900}>
        <Table
          columns={paymentStatementColumns}
          dataSource={selectedPaymentStatements}
          rowKey="_id"
          size="small"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
        />
      </Modal>
    </div>
  );
};
